/**
 * vmAdapters — the fixture-to-Vm bridge for the copied Design pages.
 *
 * WHY THIS FILE EXISTS. Each Design page imported its data from
 * `../fixtures/*` — `identityFields`, `toolPreview`, `uploadedTools` and friends.
 * The copies read the same SHAPES, but the values now come off the live Vm. Putting
 * the conversions here rather than inline in each page keeps the pages' JSX
 * byte-identical to the Design's apart from the one expression that replaces each
 * fixture name, which is exactly the diff the plan asks for: imports, navigate,
 * fixture reads, and nothing else.
 *
 * Nothing here invents content. Every function either forwards a real value or
 * omits the row when the backend has no value to forward.
 */
import type { FlowComponent } from '@trailblazer/shared';
import type { RefOption } from '../../api';
import type { IdentityField } from '../design/IdentityCard';

/**
 * The identity card's three rows, from the signed-in user's real profile.
 *
 * THREE ROWS IS LOAD-BEARING, not a formatting choice: `--identity-card-height`
 * (161px) is derived from exactly three label/value pairs — see the derivation in
 * IdentityCard.tsx. So there are always exactly three, and a null value becomes an
 * em dash rather than a dropped row, which would leave the fixed plate a row short
 * inside a box sized for three.
 *
 * WHICH three: UP-F02 names the pre-filled identity as name, contact address, and
 * department, so those are the three the card shows (the earlier Job Title row was
 * the design fixture's third pair, not a UP-F02 field). The card is a plain <dl>
 * with no inputs, so these are read-only and cannot be typed over, as UP-F02 requires.
 * `builderJobTitle` stays on the props for other surfaces; it is simply not one of
 * these three. The labels are the frame's own strings, colons included.
 */
export function identityFieldsFrom(props: {
  builderName: string;
  builderEmail: string | null;
  builderDepartment: string | null;
}): IdentityField[] {
  return [
    { label: 'Name:', value: props.builderName },
    { label: 'Contact:', value: props.builderEmail ?? '—' },
    { label: 'Department:', value: props.builderDepartment ?? '—' },
  ];
}

/**
 * The preview card's integration logos, from the flow's REAL connectors.
 *
 * `vm.components` is already in memory (it is fetched for the instructions step), and
 * each component carries `connector: string | null` — the connector API name, e.g.
 * `shared_office365`. Distinct, in first-appearance order (depth-first through the
 * definition), so the row reads in the order the automation actually uses them.
 *
 * HOW MANY. The Design draws four slots, and its fixture says in as many words that
 * four is this frame's example rather than a fixed count. A real flow can have any
 * number, so the count follows the data — but the 128px plate fits a bounded number
 * of 33x28 slots and the design draws no wrap or overflow treatment. Rather than
 * invent one, the list is capped at what the plate holds and the excess is reported
 * separately so the caller can decide; see `MAX_INTEGRATION_SLOTS`.
 */
export const MAX_INTEGRATION_SLOTS = 4;

export function integrationsFrom(components: FlowComponent[]): { name: string }[] {
  const seen = new Set<string>();
  const slots: { name: string }[] = [];
  for (const c of components) {
    if (!c.connector || seen.has(c.connector)) continue;
    seen.add(c.connector);
    slots.push({ name: prettyConnector(c.connector) });
  }
  return slots.slice(0, MAX_INTEGRATION_SLOTS);
}

/**
 * How many distinct connectors did NOT fit the plate. Zero for almost every flow;
 * surfaced so an overflow is never silently truncated.
 */
export function integrationOverflowCount(components: FlowComponent[]): number {
  const distinct = new Set(
    components.map((c) => c.connector).filter((c): c is string => Boolean(c)),
  );
  return Math.max(0, distinct.size - MAX_INTEGRATION_SLOTS);
}

/**
 * `shared_office365` → `Office 365`. Power Automate's connector API names are a
 * `shared_` prefix plus a squashed lowercase name; this undoes the prefix and the
 * common digit-run split so the plate shows something a person recognises. It is a
 * RENDERING of the real value, not a substitute for it — an unrecognised name comes
 * through with only the prefix removed rather than being mapped to a guess.
 */
function prettyConnector(api: string): string {
  const bare = api.replace(/^shared_/, '');
  const spaced = bare.replace(/([a-z])([0-9])/gi, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The label for a foreign-key detail (Category / Access / Department).
 *
 * The Design draws these three as plain text inputs, but the backend stores them as
 * numeric ids into `refData`, so a text input cannot represent one. The copied pages
 * render them as selects over these options and show the label; this resolves an id
 * back to its label for the read-back on P4.
 *
 * An unmatched id yields an em dash rather than the raw number: a bare `7` in a
 * review screen reads as data, and it is not.
 */
export function refLabel(options: RefOption[], id: number): string {
  return options.find((o) => o.id === id)?.label ?? '—';
}
